import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faLifeRing } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { NewTicketModal, NewTicketPrefill } from "./NewTicketModal";

interface ReportProblemButtonProps {
	prefill: NewTicketPrefill;
	className?: string;
}

/**
 * The "Report a problem" affordance for surfaces that have no context menu to append to (the lead
 * panel, the Inbox action bar — PREC-3938 decision 5): an icon button sized like its neighbours
 * that opens the shared ticket form prefilled. Renders nothing unless SUPPORT_TICKETS is on —
 * `undefined` means the flags have not loaded yet and counts as off.
 */
export const ReportProblemButton: FunctionComponent<ReportProblemButtonProps> = ({
	prefill,
	className,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const isSupportActive = useIsFeatureActive(FeaturesCodes.SUPPORT_TICKETS);

	if (isSupportActive !== true) {
		return null;
	}

	return (
		<>
			<button
				aria-label={t("support.reportProblem")}
				className={classNames(
					"inline-flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center rounded-[10px] border border-gray-200 text-text-meta transition-colors duration-200 hover:border-brand-light hover:text-brand-light",
					className
				)}
				title={t("support.reportProblem")}
				type="button"
				onClick={() => setIsOpen(true)}
			>
				<Icon icon={faLifeRing} />
			</button>
			{isOpen && <NewTicketModal prefill={prefill} onClose={() => setIsOpen(false)} />}
		</>
	);
};
